/**
 * The one status rule shared by the chat and the website (DB-1609).
 *
 * The website never computes a status itself: it asks the <dubai-brokers-chat>
 * element (`getPresence`), which answers with this function. Keep this file
 * free of imports so it can be tested on its own.
 *
 * - Online: the server reports a visible chat connection right now.
 * - Recently active: not online, but online within RECENTLY_ACTIVE_MS.
 * - Offline: anything else. Users who hide their last seen arrive without a
 *   time, so they are only ever Online or Offline.
 */

export const RECENTLY_ACTIVE_MS = 60 * 60 * 1000

export type PresenceStatus = 'online' | 'recent' | 'offline'

export interface PresenceEntry {
  status?: string
  lastActiveAt?: string | null
  // Older servers only send `lastSeen`.
  lastSeen?: string | null
}

export interface PresenceView {
  status: PresenceStatus
  label: 'Online' | 'Recently active' | 'Offline'
  lastSeenText: string | null
}

// CSS class for the avatar status dot. Not `recent`: that already names a
// positioned layout container in the sidebar styles.
export const PRESENCE_DOT_CLASS: Record<PresenceStatus, string> = {
  online: 'online',
  recent: 'recently-active',
  offline: 'offline',
}

/** The avatar's dot class; empty (no dot) when the status is unknown. */
export const presenceDotClass = (view: PresenceView | null): string => (view ? PRESENCE_DOT_CLASS[view.status] : '')

const MINUTE = 60 * 1000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

export const formatLastSeen = (iso: string, now: number): string => {
  const date = new Date(iso)
  const diffMs = now - date.getTime()

  if (diffMs < MINUTE) return 'Last seen just now'
  if (diffMs < HOUR) return `Last seen ${Math.floor(diffMs / MINUTE)}m ago`
  if (diffMs < DAY) return `Last seen ${Math.floor(diffMs / HOUR)}h ago`
  if (diffMs < 7 * DAY) return `Last seen ${Math.floor(diffMs / DAY)}d ago`
  return `Last seen ${date.toLocaleDateString()}`
}

/**
 * Null when there is no entry at all: no data yet, or statuses were cleared
 * while the socket is down. Callers show nothing then, rather than a wrong
 * "Offline".
 */
export const getPresenceStatus = (entry: PresenceEntry | null | undefined, now: number): PresenceView | null => {
  if (!entry) return null

  if (entry.status === 'online') {
    return { status: 'online', label: 'Online', lastSeenText: null }
  }

  const iso = entry.lastActiveAt ?? entry.lastSeen ?? null
  const time = iso ? new Date(iso).getTime() : Number.NaN
  if (!iso || Number.isNaN(time)) {
    return { status: 'offline', label: 'Offline', lastSeenText: null }
  }

  const lastSeenText = formatLastSeen(iso, now)
  return now - time <= RECENTLY_ACTIVE_MS
    ? { status: 'recent', label: 'Recently active', lastSeenText }
    : { status: 'offline', label: 'Offline', lastSeenText }
}

const STATUS_RANK: Record<PresenceStatus, number> = { online: 2, recent: 1, offline: 0 }

const lastActiveTime = (entry: PresenceEntry): number => {
  const time = new Date(entry.lastActiveAt ?? entry.lastSeen ?? '').getTime()
  return Number.isNaN(time) ? -Infinity : time
}

/**
 * The status to show for one agent when several chat accounts share their
 * hashkey (an old duplicate account, for instance): the most active one, so a
 * stale duplicate never hides the account actually in use. Online beats
 * recently active beats offline; a tie goes to the later last-active time.
 * Null when none of the accounts has a status yet.
 */
export const pickPresence = (entries: Array<PresenceEntry | null | undefined>, now: number): PresenceView | null => {
  let best: { view: PresenceView; time: number } | null = null
  for (const entry of entries) {
    const view = getPresenceStatus(entry, now)
    if (!entry || !view) continue
    const time = lastActiveTime(entry)
    if (
      !best ||
      STATUS_RANK[view.status] > STATUS_RANK[best.view.status] ||
      (STATUS_RANK[view.status] === STATUS_RANK[best.view.status] && time > best.time)
    ) {
      best = { view, time }
    }
  }
  return best ? best.view : null
}
